import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Box, CircularProgress, Typography, Alert, Button } from "@mui/material";
import endpoints from "../api/endpoints";

export default function VerifyCompanyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading: true,
    error: null,
    message: null,
    autoApproved: null,
  });

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setState({
        loading: false,
        error: "Missing verification token.",
        message: null,
        autoApproved: null,
      });
      return;
    }

    const verify = async () => {
      try {
        const res = await fetch(
          `${endpoints.COMPANY_CLAIM_VERIFY}?token=${encodeURIComponent(token)}`
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setState({
            loading: false,
            error: data?.detail || "Verification link is invalid or has expired.",
            message: null,
            autoApproved: null,
          });
          return;
        }
        setState({
          loading: false,
          error: null,
          message: data.detail || "Your email has been verified.",
          autoApproved: Boolean(data.auto_approved),
        });
      } catch {
        setState({
          loading: false,
          error: "Network error during verification. Please try again.",
          message: null,
          autoApproved: null,
        });
      }
    };

    verify();
  }, [searchParams]);

  if (state.loading) {
    return (
      <Box
        sx={{
          minHeight: "50vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 600,
        mx: "auto",
        mt: 4,
        px: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="h4" gutterBottom>
        Verify company email
      </Typography>

      {state.error && <Alert severity="error">{state.error}</Alert>}

      {!state.error && state.message && (
        <Alert severity={state.autoApproved ? "success" : "info"}>{state.message}</Alert>
      )}

      {state.autoApproved && (
        <Typography variant="body2">
          Your claim has been automatically approved because your email domain matches the company
          website.
        </Typography>
      )}

      {!state.autoApproved && !state.error && (
        <Typography variant="body2">
          Your email has been verified. Our team will now review your claim and confirm access.
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={() => navigate("/")}>
          Back to home
        </Button>
      </Box>
    </Box>
  );
}
